// In-app meeting recorder (web, PWA, the Mac app): start an upload, PUT
// audio chunks as the MediaRecorder produces them, ask which parts arrived,
// finish (→ transcription + the agent writes the notes), retry a failed
// transcription, and stream the audio back. The recorder itself
// (features/meetings/recorder/recorderEngine.ts) decides what to retry.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { Meeting } from "@/api/meetings";
import type { components } from "@/types/api.gen";

export type RecordingStart = components["schemas"]["RecordingStart"];
export type RecordingChunkAck = components["schemas"]["RecordingChunkAck"];
// `continue_recording` has a server default (false); the generated type marks it required.
export type RecordingStartBody = Omit<components["schemas"]["RecordingStartBody"], "continue_recording"> & {
  continue_recording?: boolean;
};
export type RecordingUploadState = components["schemas"]["RecordingUploadState"];
export type RecordingInterruption = components["schemas"]["RecordingInterruption"];
export type RecordingSegment = components["schemas"]["RecordingSegment"];
/** `allow_gaps` has a server default (false); the generated type marks it required. */
export type RecordingFinishBody = Omit<components["schemas"]["RecordingFinishBody"], "allow_gaps"> & { allow_gaps?: boolean };

const base = (c: string) => `/api/customers/${c}/meetings`;
const enc = encodeURIComponent;

export function startRecording(customerId: string, body: RecordingStartBody, signal?: AbortSignal): Promise<RecordingStart> {
  return apiFetch<RecordingStart>(`${base(customerId)}/recordings`, { method: "POST", body, signal });
}

/** A chunk PUT the server refused, or that never got an answer (status 0). */
export class ChunkError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.name = "ChunkError";
    this.status = status;
    this.code = code;
  }
}

/** One chunk; the server keeps chunks by index and concatenates on finish,
 * so retries can simply re-PUT the same index. `keepalive` lets a small last
 * part outlive the page (pagehide); `timeoutMs` stops one hung upload from
 * holding the whole queue. */
export async function putChunk(
  customerId: string, meetingId: string, uploadId: string, index: number, blob: Blob,
  opts: { signal?: AbortSignal; keepalive?: boolean; timeoutMs?: number } = {},
): Promise<RecordingChunkAck> {
  const timeout = opts.timeoutMs ? AbortSignal.timeout?.(opts.timeoutMs) : undefined;
  const signal = opts.signal && timeout && "any" in AbortSignal
    ? (AbortSignal as unknown as { any: (s: AbortSignal[]) => AbortSignal }).any([opts.signal, timeout])
    : (opts.signal ?? timeout);
  let resp: Response;
  try {
    resp = await fetch(`${base(customerId)}/${enc(meetingId)}/recordings/${uploadId}/chunks/${index}`, {
      method: "PUT",
      body: blob,
      credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      keepalive: opts.keepalive === true,
      signal,
    });
  } catch (e) {
    throw new ChunkError(0, null, `chunk ${index}: ${(e as Error).message || "no answer"}`);
  }
  if (!resp.ok) {
    let code: string | null = null;
    try {
      const body = (await resp.json()) as { detail?: { error?: unknown } };
      code = typeof body?.detail?.error === "string" ? body.detail.error : null;
    } catch { /* not JSON: an edge page */ }
    throw new ChunkError(resp.status, code, `chunk ${index}: HTTP ${resp.status}`);
  }
  return (await resp.json()) as RecordingChunkAck;
}

/** Idempotent on the server: a repeat after a lost answer gets the meeting again. */
export function finishRecording(
  customerId: string, meetingId: string, uploadId: string, body: RecordingFinishBody, signal?: AbortSignal,
): Promise<Meeting> {
  return apiFetch<Meeting>(`${base(customerId)}/${enc(meetingId)}/recordings/${uploadId}/finish`, { method: "POST", body, signal });
}

/** Which parts of an upload the server holds, and which are missing. */
export function getRecordingUpload(customerId: string, meetingId: string, uploadId: string, signal?: AbortSignal): Promise<RecordingUploadState> {
  return apiFetch<RecordingUploadState>(`${base(customerId)}/${enc(meetingId)}/recordings/${enc(uploadId)}`, { signal });
}

/** One recorder event for the server's log (the signed-in log sink /api/diag; metadata only, never audio). Never throws. */
export function postRecorderDiag(customerId: string, entry: object): void {
  try {
    void fetch("/api/diag", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "recorder", platform: "web", at: new Date().toISOString(), customer: customerId, ...entry }),
    }).catch(() => undefined);
  } catch { /* no fetch: nothing to tell */ }
}

export function getMeeting(customerId: string, meetingId: string, signal?: AbortSignal): Promise<Meeting> {
  return apiFetch<Meeting>(`${base(customerId)}/${enc(meetingId)}`, { signal });
}

export function retryTranscription(customerId: string, meetingId: string): Promise<Meeting> {
  return apiFetch<Meeting>(`${base(customerId)}/${enc(meetingId)}/recordings/transcribe`, { method: "POST" });
}

export const recordingFileUrl = (customerId: string, meetingId: string) =>
  `${base(customerId)}/${enc(meetingId)}/recordings/file`;

/** errorHandled: the caller shows the failure itself (the open meeting
 * does; the recorder leaves it to the app-wide toast). */
export function useRetryTranscription(use: { errorHandled?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta: use.errorHandled ? { errorHandled: true } : undefined,
    mutationFn: ({ customerId, meetingId }: { customerId: string; meetingId: string }) =>
      retryTranscription(customerId, meetingId),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["meetings", v.customerId] }),
  });
}
